import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { clampRect } from './support-image-editor.geometry';
import { SUPPORT_IMAGE_MAX_WIDTH } from './support-image.service';
import { SupportPanelService } from './support-panel.service';
import { SNIP_OVERLAY_ATTRIBUTE, SnipLabels, SnipRect, SupportSnipOverlay } from './support-snip-overlay';

/** Het deel van modern-screenshot dat we gebruiken; de rest van de module blijft in de lazy chunk. */
type ScreenshotOptions = {
  scale?: number;
  width?: number;
  height?: number;
  filter?: (el: Node) => boolean;
  features?: { restoreScrollPosition?: boolean };
  /** Wordt op de kloon van de root gezet vóór het renderen. */
  style?: { transform?: string };
};
type ScreenshotModule = {
  domToCanvas: (node: Node, options?: ScreenshotOptions) => Promise<HTMLCanvasElement>;
};

/** Wat de service van de overlay nodig heeft; in specs vervangbaar door een stub. */
export type SnipOverlay = Pick<SupportSnipOverlay, 'select' | 'setBusy' | 'hide' | 'show' | 'destroy'>;

/** Schaal van canvas-pixels per viewport-CSS-pixel, per as (tab-capture kan per as verschillen door afronding). */
type CaptureScale = { x: number; y: number };
type Capture = { canvas: HTMLCanvasElement; scale: CaptureScale };

/** `navigator.userAgentData` zit (nog) niet in de TS-lib; alleen Chromium-browsers hebben het. */
type UserAgentData = { brands?: { brand: string }[] };

/**
 * De DOM-render (modern-screenshot) heeft op de chartpagina (bodygraph + mandala,
 * duizenden SVG-nodes) tientallen seconden nodig; op lichte pagina's onder de seconde.
 */
const DOM_RENDER_TIMEOUT_MS = 25000;
/** Het native pad omvat de deeldialoog van de browser, die open kan blijven staan. */
const NATIVE_CAPTURE_TIMEOUT_MS = 15000;
/** Iets boven de 200 ms van de paneeltransitie; bij prefers-reduced-motion komt er geen transitionend. */
const PANEL_TRANSITION_FALLBACK_MS = 300;
const PANEL_SELECTOR = '.support-panel';
const PANEL_NODE_NAME = 'SUPPORT-PANEL';
/** ngx-toastr: ligt met zijn z-index boven de overlay en hoort niet in de screenshot. */
const TOAST_CONTAINER_CLASS = 'toast-container';
const MAX_CAPTURE_SCALE = 2;
/** Frames die na hide() (en na het starten van de video) verstrijken vóór het beeld gegrepen wordt. */
const SETTLE_FRAMES = 2;

/**
 * Alleen Chromium kent `preferCurrentTab`, waardoor de opname exact het zichtbare
 * tabblad is en het kader 1-op-1 te mappen is. In Firefox/Safari kiest de gebruiker
 * een venster of scherm; die mapping klopt niet, dus daar blijft de DOM-render.
 * Chromium-opties buiten de TS-lib (preferCurrentTab, selfBrowserSurface, ...) via de cast.
 */
const TAB_CAPTURE_OPTIONS = {
  video: { displaySurface: 'browser' },
  audio: false,
  preferCurrentTab: true,
  selfBrowserSurface: 'include',
  surfaceSwitching: 'exclude',
  monitorTypeSurfaces: 'exclude',
} as DisplayMediaStreamOptions;

/** De gebruiker brak de snip-modus af (Escape of annuleerknop); geen melding nodig. */
export class CaptureCancelledError extends Error {
  constructor() {
    super('cancelled');
    this.name = 'CaptureCancelledError';
  }
}

/**
 * Houdt de support-UI buiten de screenshot: het paneel (schil met aside én
 * handle), de snip-overlay en toasts. Kinderen van een geweigerd element worden niet bezocht.
 */
export function excludeSupportUi(node: Node): boolean {
  if (node.nodeName === PANEL_NODE_NAME) return false;
  if (!(node instanceof Element)) return true;
  return !node.hasAttribute(SNIP_OVERLAY_ATTRIBUTE) && !node.classList.contains(TOAST_CONTAINER_CLASS);
}

/** Chromium-familie (Chrome, Edge, Brave, ...); Opera valt in de UA-fallback buiten de boot. */
export function isChromiumBrowser(nav: Navigator = navigator): boolean {
  const brands = (nav as Navigator & { userAgentData?: UserAgentData }).userAgentData?.brands;
  if (brands) return brands.some(b => /Chromium|Google Chrome|Microsoft Edge/.test(b.brand));
  return /Chrome\//.test(nav.userAgent) && !/OPR\//.test(nav.userAgent);
}

/** De gebruiker sloot de deeldialoog zonder te delen: geen fout, wel terugval. */
function isDismissal(error: unknown): boolean {
  return error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'AbortError');
}

function nextFrame(): Promise<void> {
  return new Promise(resolve => requestAnimationFrame(() => resolve()));
}

/**
 * Tekent één frame van de stream op een canvas van videoWidth x videoHeight.
 * Chromium levert nieuwe frames alleen bij wijzigingen; requestVideoFrameCallback
 * wacht op écht nieuwe frames (na het verbergen van de overlay), rAF is de terugval.
 */
async function grabVideoFrame(stream: MediaStream): Promise<HTMLCanvasElement> {
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;
  try {
    await video.play();
    if (!(video.videoWidth > 0)) {
      await new Promise<void>(resolve => video.addEventListener('loadedmetadata', () => resolve(), { once: true }));
    }
    for (let i = 0; i < SETTLE_FRAMES; i++) await nextVideoFrame(video);
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    return canvas;
  } finally {
    video.pause();
    video.srcObject = null;
  }
}

function nextVideoFrame(video: HTMLVideoElement): Promise<void> {
  if (typeof video.requestVideoFrameCallback !== 'function') return nextFrame();
  return new Promise(resolve => video.requestVideoFrameCallback(() => resolve()));
}

/**
 * Maakt een screenshot van (een gebied van) de pagina zonder het support-paneel,
 * zijn handle en de snip-overlay. Twee routes:
 *
 * 1. Chromium: native tab-capture via getDisplayMedia (deeldialoog met het huidige
 *    tabblad voorgeselecteerd; één klik op Delen). Pixel-perfect en snel, ook op
 *    zware chartpagina's. Annuleren van de dialoog, een fout of een time-out valt
 *    terug op route 2.
 * 2. DOM-render met modern-screenshot (pas bij de eerste capture geladen, eigen
 *    chunk). Werkt overal, maar is op pagina's met duizenden SVG-nodes traag.
 */
@Injectable({ providedIn: 'root' })
export class SupportScreenshotService {
  private readonly panel = inject(SupportPanelService);
  private readonly transloco = inject(TranslocoService);

  /** Overschrijfbaar in specs; de echte import levert de lazy chunk. */
  protected loader: () => Promise<ScreenshotModule> = () => import('modern-screenshot');
  /** Overschrijfbaar in specs om zonder pointer-events te kiezen. */
  protected createOverlay: (labels: SnipLabels) => SnipOverlay = labels => new SupportSnipOverlay(document, labels);
  /** Overschrijfbaar in specs om het niet-Chromium-pad te testen. */
  protected isChromium: () => boolean = () => isChromiumBrowser();
  /** Overschrijfbaar in specs zodat er geen echte MediaStream nodig is. */
  protected grabFrame: (stream: MediaStream) => Promise<HTMLCanvasElement> = grabVideoFrame;
  /** Overschrijfbaar in specs om de timeout-paden zonder wachten te testen. */
  protected domRenderTimeoutMs = DOM_RENDER_TIMEOUT_MS;
  protected nativeTimeoutMs = NATIVE_CAPTURE_TIMEOUT_MS;
  /** Overschrijfbaar in specs. */
  protected transitionFallbackMs = PANEL_TRANSITION_FALLBACK_MS;

  /**
   * Sluit het paneel, laat de gebruiker een gebied kiezen en levert dat als PNG.
   * Gooit CaptureCancelledError als de gebruiker annuleert.
   */
  async capturePage(): Promise<Blob> {
    this.panel.close();
    this.panel.handleHidden.set(true);
    const labels = this.labels();
    const overlay = this.createOverlay(labels);
    try {
      // Het paneel wordt via de filter toch weggelaten; het sluiten is UX, zodat
      // de gebruiker ziet wat er gefotografeerd wordt.
      await this.panelClosed();
      return await this.selectAndCapture(overlay, labels);
    } finally {
      overlay.destroy();
      this.panel.handleHidden.set(false);
      // BELANGRIJK: enkel terug open zetten, niet via openAt(). Tab en geopende
      // melding blijven zo staan, en de paneelinhoud blijft gemount terwijl het
      // paneel dicht is: een reactie in wording (tekst + bijlagen) in het detail
      // overleeft de screenshot. openAt() wiste selectedFeedbackId, waardoor
      // "Mijn meldingen" terugviel op de lijst en de reactie verloren ging
      // (zie de spec "behoudt tab én geopende melding").
      this.panel.open.set(true);
    }
  }

  /**
   * Zelfde snip-flow, maar zonder het support-paneel te sluiten: voor notities,
   * waar het kader overal op de pagina mag liggen (ook over een zwevend
   * notitievenster). Enkel de support-handle verdwijnt tijdens de capture, zodat
   * hij niet op het beeld komt. Gooit CaptureCancelledError als de gebruiker annuleert.
   */
  async captureRegion(): Promise<Blob> {
    const handleWasHidden = this.panel.handleHidden();
    this.panel.handleHidden.set(true);
    const labels = this.labels();
    const overlay = this.createOverlay(labels);
    try {
      return await this.selectAndCapture(overlay, labels);
    } finally {
      overlay.destroy();
      this.panel.handleHidden.set(handleWasHidden);
    }
  }

  /** Kader laten kiezen, vastleggen (native of DOM-render) en als PNG leveren. */
  private async selectAndCapture(overlay: SnipOverlay, labels: SnipLabels): Promise<Blob> {
    const choice = await overlay.select();
    if (choice.kind === 'cancelled') throw new CaptureCancelledError();
    const { canvas, scale } = await this.capture(overlay, labels);
    const region = choice.kind === 'region' ? this.crop(canvas, choice.rect, scale) : canvas;
    return await this.toPng(this.downscale(region));
  }

  private labels(): SnipLabels {
    return {
      hint: this.transloco.translate<string>('support.snip.hint'),
      adjustHint: this.transloco.translate<string>('support.snip.adjustHint'),
      busy: this.transloco.translate<string>('support.snip.busy'),
      busyFallback: this.transloco.translate<string>('support.snip.busyFallback'),
      shareHint: this.transloco.translate<string>('support.snip.shareHint'),
      confirm: this.transloco.translate<string>('support.snip.confirm'),
      cancel: this.transloco.translate<string>('support.snip.cancel'),
    };
  }

  private panelClosed(): Promise<void> {
    const panel = document.querySelector(PANEL_SELECTOR);
    return new Promise(resolve => {
      const done = (event?: Event): void => {
        // Een bubbelend transitionend van een kind (tab, badge) telt niet: alleen het paneel zelf.
        if (event && event.target !== panel) return;
        clearTimeout(timer);
        panel?.removeEventListener('transitionend', done);
        resolve();
      };
      const timer = setTimeout(done, this.transitionFallbackMs);
      panel?.addEventListener('transitionend', done);
    });
  }

  /**
   * Strategieketen: native tab-capture (Chromium) en anders, of bij terugval, de
   * DOM-render. Na een native poging benoemt de bezig-tekst de terugval, zodat een
   * geannuleerde deeldialoog niet als "er gebeurt niets" overkomt.
   */
  private async capture(overlay: SnipOverlay, labels: SnipLabels): Promise<Capture> {
    const attemptNative = this.nativeAvailable();
    if (attemptNative) {
      overlay.setBusy(labels.shareHint);
      try {
        return await this.withTimeout(signal => this.captureNative(overlay, labels, signal), this.nativeTimeoutMs);
      } catch (error) {
        if (!isDismissal(error)) console.warn('[support] native tab-capture mislukt, terugval op DOM-render', error);
      }
    }
    overlay.setBusy(attemptNative ? labels.busyFallback : labels.busy);
    const started = performance.now();
    const scale = this.captureScale();
    const canvas = await this.withTimeout(() => this.render(scale), this.domRenderTimeoutMs);
    console.info('[support] screenshot via DOM-render in %d ms', Math.round(performance.now() - started));
    return { canvas, scale: { x: scale, y: scale } };
  }

  private nativeAvailable(): boolean {
    return this.isChromium() && typeof navigator.mediaDevices?.getDisplayMedia === 'function';
  }

  /**
   * Vraagt de tab-stream (deeldialoog), verbergt de overlay minstens één frame,
   * grijpt een frame en stopt daarna alle tracks (ook als de time-out intussen
   * afliep: dan verdwijnt het deel-icoon van de tab meteen weer).
   */
  private async captureNative(overlay: SnipOverlay, labels: SnipLabels, signal: AbortSignal): Promise<Capture> {
    const stream = await navigator.mediaDevices.getDisplayMedia(TAB_CAPTURE_OPTIONS);
    try {
      if (signal.aborted) throw new Error('timeout');
      overlay.setBusy(labels.busy);
      overlay.hide();
      for (let i = 0; i < SETTLE_FRAMES; i++) await nextFrame();
      const canvas = await this.grabFrame(stream);
      return { canvas, scale: this.tabCaptureScale(canvas) };
    } finally {
      overlay.show();
      stream.getTracks().forEach(track => track.stop());
    }
  }

  /**
   * Chromium levert de viewport in device-pixels. Het kader is gemeten in de
   * layout-viewport (documentElement.clientWidth/Height, zonder vensterscrollbar);
   * heeft het venster toch een scrollbar, dan past de opname (inner-maten) beter
   * en kiezen we op aspect-ratio de best passende maat.
   */
  private tabCaptureScale(canvas: HTMLCanvasElement): CaptureScale {
    const layout = { width: document.documentElement.clientWidth, height: document.documentElement.clientHeight };
    const inner = { width: window.innerWidth, height: window.innerHeight };
    const aspect = canvas.width / canvas.height;
    const mismatch = (size: { width: number; height: number }): number => Math.abs(size.width / size.height - aspect);
    const viewport = mismatch(inner) < mismatch(layout) ? inner : layout;
    return { x: canvas.width / viewport.width, y: canvas.height / viewport.height };
  }

  private captureScale(): number {
    return Math.min(window.devicePixelRatio || 1, MAX_CAPTURE_SCALE);
  }

  /**
   * De viewport-afmetingen zijn verplicht: de layout van deze app is op
   * `height: 100%` gebaseerd en `document.body` meet zelf 0 px hoog, waardoor
   * modern-screenshot zonder width/height een leeg canvas (0 x 0) oplevert.
   * De layout-viewport (documentElement.clientWidth/Height, zonder eventuele
   * vensterscrollbar) is dezelfde maat waarin de overlay het kader meet.
   * restoreScrollPosition houdt gescrolde containers (drawer-content, tabellen)
   * in de kloon op dezelfde scrollpositie.
   */
  private async render(scale: number): Promise<HTMLCanvasElement> {
    const { domToCanvas } = await this.loader();
    // De app scrolt niet op vensterniveau (scroll zit in containers). Mocht het
    // venster toch gescrold zijn, dan schuift de kloon mee zodat het canvas het
    // zichtbare deel toont en het kader (viewport-coördinaten) blijft kloppen.
    const { scrollX, scrollY } = window;
    const scrolled = scrollX !== 0 || scrollY !== 0;
    return domToCanvas(document.body, {
      scale,
      width: document.documentElement.clientWidth,
      height: document.documentElement.clientHeight,
      filter: excludeSupportUi,
      features: { restoreScrollPosition: true },
      ...(scrolled ? { style: { transform: `translate(${-scrollX}px, ${-scrollY}px)` } } : {}),
    });
  }

  /** Snijdt het gekozen gebied (viewport-CSS-pixels) uit het canvas (bronpixels = CSS-pixels x schaal per as). */
  private crop(canvas: HTMLCanvasElement, rect: SnipRect, scale: CaptureScale): HTMLCanvasElement {
    const source = clampRect(
      { x: rect.x * scale.x, y: rect.y * scale.y, width: rect.width * scale.x, height: rect.height * scale.y },
      { width: canvas.width, height: canvas.height },
    );
    const x = Math.round(source.x);
    const y = Math.round(source.y);
    const width = Math.max(1, Math.round(source.width));
    const height = Math.max(1, Math.round(source.height));
    const cropped = document.createElement('canvas');
    cropped.width = width;
    cropped.height = height;
    cropped.getContext('2d')?.drawImage(canvas, x, y, width, height, 0, 0, width, height);
    return cropped;
  }

  /**
   * Bij een timeout loopt het werk op de achtergrond door (domToCanvas heeft geen
   * annuleer-API); het signaal laat het native pad een laat toegekende stream
   * meteen weer stoppen. Bij succes wordt de timer geruimd.
   */
  private withTimeout<T>(work: (signal: AbortSignal) => Promise<T>, ms: number): Promise<T> {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new Error('timeout'));
      }, ms);
    });
    return Promise.race([work(controller.signal), timeout]).finally(() => clearTimeout(timer));
  }

  /** Meteen naar max 1920 breed: een 2x-scherm levert anders een canvas van tientallen MB. */
  private downscale(canvas: HTMLCanvasElement): HTMLCanvasElement {
    if (canvas.width <= SUPPORT_IMAGE_MAX_WIDTH) return canvas;
    const scaled = document.createElement('canvas');
    scaled.width = SUPPORT_IMAGE_MAX_WIDTH;
    scaled.height = Math.max(1, Math.round((canvas.height * SUPPORT_IMAGE_MAX_WIDTH) / canvas.width));
    scaled.getContext('2d')?.drawImage(canvas, 0, 0, scaled.width, scaled.height);
    return scaled;
  }

  private toPng(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('empty'))), 'image/png');
    });
  }
}
